/**
 * The canonical fan curve — Screen System §3, frame `6e`.
 *
 * Shared by OrbFan and by the brand site's live demo, because a curve that is
 * "the number stated in the document" in one place and "close enough" in
 * another is how a system stops being one system.
 *
 * The drawing positions items absolutely on a 430×880 frame:
 *
 *   y       706 (nearest the Orb) → 178
 *   offset  20 + 78·sin(π·u)   where u = (706 − y) / 528
 *
 * The leading 20 is the Orb's own inset, which CSS already applies via
 * `right: var(--hottr-orb-inset)`. So what this module returns is the CURVE
 * ONLY — the 78·sin term — which chassis.css consumes as `--arc-x`.
 *
 * Why a curve at all: HOTTR is a rigid, typographic, grid-based system, and
 * the Orb is the one organic object in it. The fan is allowed to break the
 * grid because you are touching the Orb while it does. A second curved element
 * anywhere else would spend the exception for nothing.
 *
 * The words never bend. Positions describe an arc; every label stays
 * horizontal, which is why this returns an x offset and never a rotation.
 */

/** y of the item nearest the Orb, on the reference frame. */
export const FAN_Y_ORIGIN = 706;
/** y span the sine is described over. */
export const FAN_CURVE_THROW = 528;
/** Maximum x offset at the curve's belly. */
export const FAN_CURVE_DEPTH = 78;

/** Route rhythm — also what keeps each hit area above the 44px floor (§5). */
export const FAN_ROUTE_PITCH = 75;
/** Context pair spacing. */
export const FAN_CONTEXT_PITCH = 52;
/** Clear space between the last route and the hairline. */
export const FAN_HAIRLINE_GAP = 42;

/**
 * The curve's x offset at a given y on the reference frame, in px.
 *
 * `u` is clamped: items beyond the drawn throw sit at the curve's ends rather
 * than swinging back inward, which is what an unclamped sine would do to a
 * world with an unusually long route list.
 */
export const fanArcX = (y: number): number => {
  const u = Math.min(1, Math.max(0, (FAN_Y_ORIGIN - y) / FAN_CURVE_THROW));
  return FAN_CURVE_DEPTH * Math.sin(Math.PI * u);
};

export interface FanOffsets {
  /** One px offset per route, in DOM order (furthest from the Orb first). */
  routes: number[];
  /** One px offset per context action, in DOM order. */
  context: number[];
  /** Offset for the separating hairline. */
  hairline: number;
}

/**
 * Every offset the fan needs, for a given number of routes and context
 * actions. Counting is done from the Orb outward because that is the axis the
 * drawing is measured on — the item nearest the thumb is item 0 regardless of
 * how many sit above it.
 */
export const fanOffsets = (routeCount: number, contextCount: number): FanOffsets => {
  // DOM order is top-to-bottom; the Orb is at the bottom. So the last route in
  // the list is the one nearest it, and HOTTIE — the fixed tail's end — lands
  // closest to the Orb it belongs to.
  const routes = Array.from({ length: routeCount }, (_, i) => {
    const fromOrb = routeCount - 1 - i;
    return fanArcX(FAN_Y_ORIGIN - fromOrb * FAN_ROUTE_PITCH);
  });

  const furthestRouteY = FAN_Y_ORIGIN - Math.max(0, routeCount - 1) * FAN_ROUTE_PITCH;
  const hairlineY = furthestRouteY - FAN_HAIRLINE_GAP;

  const context = Array.from({ length: contextCount }, (_, i) => {
    const fromHairline = contextCount - 1 - i;
    return fanArcX(hairlineY - (fromHairline + 1) * FAN_CONTEXT_PITCH);
  });

  return { routes, context, hairline: fanArcX(hairlineY) };
};

/**
 * The energy path — the thin pink line the fan hangs off.
 *
 * Screen System §1 says the Orb is the single persistent control, and the Orb
 * language allows thin energy paths to extend outward while the Orb stays
 * dominant. Before this existed the fan read as "Orb, plus some labels": four
 * absolutely-positioned links that happened to appear near a button. The line
 * is what makes it read as "the Orb has opened HOTTR" — navigation with a
 * visible source.
 *
 * It is deliberately NOT a border, a tube or a container. One 1.5px signal
 * path, drawn through the SAME sine the labels are positioned on, so the line
 * and the labels cannot disagree. That is the whole reason this lives here
 * next to `fanArcX` rather than being eyeballed in CSS.
 */
export interface FanPath {
  /** SVG path data, in the local space described by `width` × `height`. */
  d: string;
  /** Local space width — the curve's depth plus the stroke's own room. */
  width: number;
  /** Local space height — Orb end to the furthest item, plus the lead-in. */
  height: number;
}

/** How far below the first item the path runs, to meet the Orb. */
export const FAN_PATH_LEAD = 34;
/** Sampling step. 6px is under a stroke width at any real fan size. */
const FAN_PATH_STEP = 6;

/**
 * Trace the curve from the Orb (bottom) to the furthest item (top).
 *
 * `x` is distance from the anchored screen edge, matching `--arc-x`; the
 * caller mirrors it for a left-handed Orb exactly as it mirrors the labels.
 * `y` is SVG-down, so the Orb end is at `height` and the far end at 0.
 */
export const fanPath = (routeCount: number, contextCount: number): FanPath => {
  const topFromOrb =
    contextCount > 0
      ? Math.max(0, routeCount - 1) * FAN_ROUTE_PITCH +
        FAN_HAIRLINE_GAP +
        contextCount * FAN_CONTEXT_PITCH
      : Math.max(0, routeCount - 1) * FAN_ROUTE_PITCH;

  const height = topFromOrb + FAN_PATH_LEAD;
  const points: string[] = [];

  for (let d = -FAN_PATH_LEAD; d <= topFromOrb; d += FAN_PATH_STEP) {
    const x = fanArcX(FAN_Y_ORIGIN - Math.max(0, d));
    points.push(`${x.toFixed(2)},${(height - (d + FAN_PATH_LEAD)).toFixed(2)}`);
  }
  // Always land exactly on the furthest item rather than wherever the step fell.
  const endX = fanArcX(FAN_Y_ORIGIN - topFromOrb);
  points.push(`${endX.toFixed(2)},0`);

  return {
    d: `M ${points.join(' L ')}`,
    width: FAN_CURVE_DEPTH + 8,
    height,
  };
};
